/**
 * Strict allowlisted rebuild of owner question-answer trace list/detail payloads.
 * Never spreads server objects; rejects forbidden identity keys anywhere in the tree.
 */

import {
  deriveCognitoryActivityEvaluationStatus,
  parseCognitoryActivityUserEvaluation,
  type CognitoryActivityEvaluationStatus,
  type CognitoryActivityUserEvaluationView,
} from "../presentation/cognitoryActivityEvaluationStatus";

export const COGNITORY_ACTIVITY_TRACE_SCHEMA_VERSION =
  "cognitory_question_answer_trace_v1" as const;

export const COGNITORY_ACTIVITY_ATTENTION_CLASSES = [
  "answered",
  "governed_refusal",
  "verification_rejection",
  "operational_failure",
] as const;

export type CognitoryActivityAttentionClass =
  (typeof COGNITORY_ACTIVITY_ATTENTION_CLASSES)[number];

export type CognitoryActivitySafeCitationView = {
  citationLabel: string;
};

export type CognitoryActivityDiagnosticsView = {
  embeddingCallCount?: number;
  pineconeQueryCount?: number;
  modelCallCount?: number;
  returnedMatchCount?: number;
  acceptedEvidenceItemCount?: number;
  retrievalDecision?: string;
  packetDecision?: string;
  claimVerificationDecision?: string;
  rendererDecision?: string;
  refusalStage?: string;
  recommendedRefusalReason?: string;
  internalFailure?: string;
  detail?: string;
};

export type CognitoryActivityResponseView =
  | {
      kind: "answer_rendered";
      answerText: string;
      citations: CognitoryActivitySafeCitationView[];
      verifiedClaimCount: number;
      renderedCitationCount: number;
    }
  | {
      kind: "refusal_rendered";
      refusalText: string;
      citations: CognitoryActivitySafeCitationView[];
      verifiedClaimCount: number;
      renderedCitationCount: number;
    }
  | {
      kind: "error_response";
      decision: string;
    };

export type CognitoryActivityUserEvaluationDetailView =
  CognitoryActivityUserEvaluationView & {
    createdAt: string;
    comment?: string;
  };

export type CognitoryActivitySummaryView = {
  traceRequestId: string;
  capturedAt: string;
  expiresAt: string;
  durationMs: number;
  httpStatus: 200 | 422 | 503;
  responseDecision: string;
  executionDecision: string;
  attentionClass: CognitoryActivityAttentionClass;
  needsAttention: boolean;
  question: string;
  authorLabel: "You" | "Another authorized user";
  evaluationStatus: CognitoryActivityEvaluationStatus;
};

export type CognitoryActivityDetailView = CognitoryActivitySummaryView & {
  schemaVersion: typeof COGNITORY_ACTIVITY_TRACE_SCHEMA_VERSION;
  response: CognitoryActivityResponseView;
  diagnostics?: CognitoryActivityDiagnosticsView;
  userEvaluation: CognitoryActivityUserEvaluationDetailView | null;
};

export type CognitoryActivityListRebuildResult =
  | {
      kind: "success";
      supportReference: string;
      cognitoryId: string;
      limit: number;
      items: CognitoryActivitySummaryView[];
      nextCursor: string | null;
    }
  | { kind: "malformed_success"; supportReference: string | null };

export type CognitoryActivityDetailRebuildResult =
  | {
      kind: "success";
      supportReference: string;
      trace: CognitoryActivityDetailView;
    }
  | { kind: "malformed_success"; supportReference: string | null };

const V2_ID_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const REQUEST_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._:-]*$/;
const CITATION_LABEL_PATTERN = /^\[\^[1-9][0-9]*\]$/;
const RENDER_SHA256_PATTERN = /^[a-f0-9]{64}$/i;
const AUTHOR_UID_SHA256_PATTERN = /^[a-f0-9]{64}$/;

const MAX_QUESTION_CHARS = 16_384;
const MAX_ANSWER_CHARS = 200_000;
const MAX_REFUSAL_CHARS = 200_000;
const MAX_TEXT_FIELD = 4_096;
const MAX_CURSOR_CHARS = 4_096;

const ATTENTION_CLASS_SET = new Set<string>(
  COGNITORY_ACTIVITY_ATTENTION_CLASSES,
);

const EXECUTION_DECISIONS = new Set<string>([
  "answer_rendered",
  "refusal_rendered",
  "evidence_unavailable",
  "proposal_invalid",
  "claim_verification_failed",
  "renderer_failed",
  "provider_failed",
  "temporary_failure",
  "not_run",
]);

const ERROR_RESPONSE_DECISIONS = new Set<string>([
  "unauthenticated",
  "forbidden",
  "invalid_request",
  "cognitory_unavailable",
  "evidence_unavailable",
  "answer_not_supported",
  "temporary_failure",
]);

const DIAGNOSTICS_COUNT_FIELDS = new Set<string>([
  "embeddingCallCount",
  "pineconeQueryCount",
  "modelCallCount",
  "returnedMatchCount",
  "acceptedEvidenceItemCount",
]);

const DIAGNOSTICS_STRING_FIELDS = [
  "retrievalDecision",
  "packetDecision",
  "claimVerificationDecision",
  "rendererDecision",
  "refusalStage",
  "recommendedRefusalReason",
  "internalFailure",
  "detail",
] as const;

const SUMMARY_ITEM_KEYS = [
  "traceRequestId",
  "cognitoryId",
  "capturedAt",
  "expiresAt",
  "durationMs",
  "httpStatus",
  "responseDecision",
  "executionDecision",
  "attentionClass",
  "needsAttention",
  "question",
  "authorUidSha256",
  "isYou",
  "userEvaluation",
] as const;

const DETAIL_TRACE_ALLOWED_KEYS = [
  ...SUMMARY_ITEM_KEYS,
  "schemaVersion",
  "response",
  "conversationContext",
  "diagnostics",
] as const;

function parseUserEvaluationDetail(
  value: unknown,
): CognitoryActivityUserEvaluationDetailView | null {
  if (value === null) {
    return null;
  }
  const summary = parseCognitoryActivityUserEvaluation(value);
  if (!summary) {
    return null;
  }
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  const createdAt = String(record.createdAt ?? "");
  if (createdAt.length === 0) {
    return null;
  }
  let comment: string | undefined;
  if (record.comment !== undefined && record.comment !== null) {
    if (typeof record.comment !== "string") {
      return null;
    }
    const trimmed = record.comment.trim();
    if (trimmed.length > 0) {
      comment = trimmed;
    }
  }
  return {
    ...summary,
    createdAt,
    ...(comment !== undefined ? { comment } : {}),
  };
}

const FORBIDDEN_TREE_KEYS = new Set<string>(["canonicalUserId"]);

function keysAreOnlyAllowed(
  record: Record<string, unknown>,
  allowed: readonly string[],
): boolean {
  const allowedSet = new Set<string>(allowed);
  return Object.keys(record).every(key => allowedSet.has(key));
}

function keysMatchExactly(
  record: Record<string, unknown>,
  allowed: readonly string[],
): boolean {
  if (!keysAreOnlyAllowed(record, allowed)) {
    return false;
  }
  return Object.keys(record).length === allowed.length;
}

function treeContainsForbiddenKey(value: unknown): boolean {
  if (value === null || typeof value !== "object") {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some(entry => treeContainsForbiddenKey(entry));
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (FORBIDDEN_TREE_KEYS.has(key)) {
      return true;
    }
    if (treeContainsForbiddenKey(record[key])) {
      return true;
    }
  }
  return false;
}

function isIsoString(value: unknown): value is string {
  if (typeof value !== "string" || !value.trim()) {
    return false;
  }
  return Number.isFinite(Date.parse(value));
}

function isNonNegativeSafeInteger(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    Number.isFinite(value)
  );
}

function isValidV2Id(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  if (
    trimmed.length === 0 ||
    trimmed.length > 128 ||
    trimmed.includes("..") ||
    trimmed.includes("/")
  ) {
    return false;
  }
  return V2_ID_PATTERN.test(trimmed);
}

function isValidRequestId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= 128 &&
    REQUEST_ID_PATTERN.test(value)
  );
}

function isHttpStatus(value: unknown): value is 200 | 422 | 503 {
  return value === 200 || value === 422 || value === 503;
}

function boundedString(value: unknown, max: number): string | null {
  if (typeof value !== "string" || value.length === 0 || value.length > max) {
    return null;
  }
  return value;
}

function isValidAuthorUidSha256(value: unknown): boolean {
  return typeof value === "string" && AUTHOR_UID_SHA256_PATTERN.test(value);
}

function rebuildSafeCitation(
  value: unknown,
): CognitoryActivitySafeCitationView | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (
    typeof record.citationLabel !== "string" ||
    !CITATION_LABEL_PATTERN.test(record.citationLabel)
  ) {
    return null;
  }
  return { citationLabel: record.citationLabel };
}

function rebuildDiagnostics(
  value: unknown,
): CognitoryActivityDiagnosticsView | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  const view: CognitoryActivityDiagnosticsView = {};

  for (const field of DIAGNOSTICS_COUNT_FIELDS) {
    if (record[field] === undefined) {
      continue;
    }
    if (!isNonNegativeSafeInteger(record[field])) {
      return null;
    }
    (view as Record<string, number>)[field] = record[field] as number;
  }

  for (const field of DIAGNOSTICS_STRING_FIELDS) {
    if (record[field] === undefined) {
      continue;
    }
    const parsed = boundedString(record[field], MAX_TEXT_FIELD);
    if (!parsed) {
      return null;
    }
    view[field] = parsed;
  }

  return view;
}

function hasOnlyKeys(
  record: Record<string, unknown>,
  allowed: readonly string[],
): boolean {
  return keysAreOnlyAllowed(record, allowed);
}

function rebuildResponseView(
  value: unknown,
  expectedTraceRequestId: string,
): CognitoryActivityResponseView | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  const decision = record.decision;
  const requestId = record.requestId;
  if (!isValidRequestId(requestId) || requestId !== expectedTraceRequestId) {
    return null;
  }

  if (decision === "answer_rendered") {
    const allowed = [
      "decision",
      "requestId",
      "answerMode",
      "answerText",
      "citations",
      "verifiedClaimCount",
      "renderedCitationCount",
      "deterministicRenderSha256",
      "conversationScope",
    ];
    if (!hasOnlyKeys(record, allowed)) {
      return null;
    }
    const answerText = boundedString(record.answerText, MAX_ANSWER_CHARS);
    if (!answerText) {
      return null;
    }
    if (
      typeof record.answerMode !== "string" ||
      record.answerMode.length > 64
    ) {
      return null;
    }
    if (
      typeof record.deterministicRenderSha256 !== "string" ||
      !RENDER_SHA256_PATTERN.test(record.deterministicRenderSha256)
    ) {
      return null;
    }
    if (
      !isNonNegativeSafeInteger(record.verifiedClaimCount) ||
      !isNonNegativeSafeInteger(record.renderedCitationCount)
    ) {
      return null;
    }
    if (!Array.isArray(record.citations)) {
      return null;
    }
    const citations: CognitoryActivitySafeCitationView[] = [];
    for (const item of record.citations) {
      const rebuilt = rebuildSafeCitation(item);
      if (!rebuilt) {
        return null;
      }
      citations.push(rebuilt);
    }
    if (citations.length !== record.renderedCitationCount) {
      return null;
    }
    return {
      kind: "answer_rendered",
      answerText,
      citations,
      verifiedClaimCount: record.verifiedClaimCount,
      renderedCitationCount: record.renderedCitationCount,
    };
  }

  if (decision === "refusal_rendered") {
    const allowed = [
      "decision",
      "requestId",
      "answerMode",
      "refusalText",
      "supportDetails",
      "verifiedClaimCount",
      "renderedCitationCount",
      "citations",
      "deterministicRenderSha256",
      "conversationScope",
    ];
    if (!hasOnlyKeys(record, allowed)) {
      return null;
    }
    const refusalText = boundedString(record.refusalText, MAX_REFUSAL_CHARS);
    if (!refusalText) {
      return null;
    }
    if (
      !isNonNegativeSafeInteger(record.verifiedClaimCount) ||
      !isNonNegativeSafeInteger(record.renderedCitationCount)
    ) {
      return null;
    }
    if (
      typeof record.deterministicRenderSha256 !== "string" ||
      !RENDER_SHA256_PATTERN.test(record.deterministicRenderSha256)
    ) {
      return null;
    }
    const citations: CognitoryActivitySafeCitationView[] = [];
    if (!Array.isArray(record.citations)) {
      return null;
    }
    for (const item of record.citations) {
      const rebuilt = rebuildSafeCitation(item);
      if (!rebuilt) {
        return null;
      }
      citations.push(rebuilt);
    }
    if (citations.length !== record.renderedCitationCount) {
      return null;
    }
    return {
      kind: "refusal_rendered",
      refusalText,
      citations,
      verifiedClaimCount: record.verifiedClaimCount,
      renderedCitationCount: record.renderedCitationCount,
    };
  }

  if (typeof decision === "string" && ERROR_RESPONSE_DECISIONS.has(decision)) {
    const allowed = ["decision", "requestId", "conversationScope"];
    if (!hasOnlyKeys(record, allowed)) {
      return null;
    }
    return { kind: "error_response", decision };
  }

  return null;
}

export function projectActivitySummaryFields(
  record: Record<string, unknown>,
  expectedCognitoryId: string,
): CognitoryActivitySummaryView | null {
  if (
    !isValidRequestId(record.traceRequestId) ||
    !isValidV2Id(record.cognitoryId) ||
    record.cognitoryId !== expectedCognitoryId
  ) {
    return null;
  }
  if (!isIsoString(record.capturedAt) || !isIsoString(record.expiresAt)) {
    return null;
  }
  if (
    !isNonNegativeSafeInteger(record.durationMs) ||
    !isHttpStatus(record.httpStatus)
  ) {
    return null;
  }
  if (
    typeof record.responseDecision !== "string" ||
    typeof record.executionDecision !== "string" ||
    !EXECUTION_DECISIONS.has(record.executionDecision)
  ) {
    return null;
  }
  if (
    typeof record.attentionClass !== "string" ||
    !ATTENTION_CLASS_SET.has(record.attentionClass)
  ) {
    return null;
  }
  if (typeof record.needsAttention !== "boolean") {
    return null;
  }
  const question = boundedString(record.question, MAX_QUESTION_CHARS);
  if (!question) {
    return null;
  }
  if (typeof record.isYou !== "boolean") {
    return null;
  }
  if (!isValidAuthorUidSha256(record.authorUidSha256)) {
    return null;
  }
  if (!("userEvaluation" in record)) {
    return null;
  }
  const userEvaluation = parseCognitoryActivityUserEvaluation(
    record.userEvaluation,
  );
  if (record.userEvaluation !== null && userEvaluation === null) {
    return null;
  }

  return {
    traceRequestId: record.traceRequestId,
    capturedAt: record.capturedAt,
    expiresAt: record.expiresAt,
    durationMs: record.durationMs,
    httpStatus: record.httpStatus,
    responseDecision: record.responseDecision,
    executionDecision: record.executionDecision,
    attentionClass: record.attentionClass as CognitoryActivityAttentionClass,
    needsAttention: record.needsAttention,
    question,
    authorLabel: record.isYou ? "You" : "Another authorized user",
    evaluationStatus: deriveCognitoryActivityEvaluationStatus(userEvaluation),
  };
}

function rebuildListSummaryItem(
  value: unknown,
  expectedCognitoryId: string,
): CognitoryActivitySummaryView | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (!keysMatchExactly(record, SUMMARY_ITEM_KEYS)) {
    return null;
  }
  return projectActivitySummaryFields(record, expectedCognitoryId);
}

function rebuildDetailTraceRecord(
  value: unknown,
  input: {
    expectedCognitoryId: string;
    expectedTraceRequestId: string;
  },
): CognitoryActivityDetailView | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (!keysAreOnlyAllowed(record, DETAIL_TRACE_ALLOWED_KEYS)) {
    return null;
  }
  if (record.schemaVersion !== COGNITORY_ACTIVITY_TRACE_SCHEMA_VERSION) {
    return null;
  }
  const summary = projectActivitySummaryFields(
    record,
    input.expectedCognitoryId,
  );
  if (!summary) {
    return null;
  }
  if (summary.traceRequestId !== input.expectedTraceRequestId) {
    return null;
  }
  const response = rebuildResponseView(
    record.response,
    input.expectedTraceRequestId,
  );
  if (!response) {
    return null;
  }
  let diagnostics: CognitoryActivityDiagnosticsView | undefined;
  if (record.diagnostics !== undefined) {
    const rebuiltDiagnostics = rebuildDiagnostics(record.diagnostics);
    if (rebuiltDiagnostics === null) {
      return null;
    }
    if (rebuiltDiagnostics && Object.keys(rebuiltDiagnostics).length > 0) {
      diagnostics = rebuiltDiagnostics;
    }
  }
  if (!("userEvaluation" in record)) {
    return null;
  }
  const userEvaluation = parseUserEvaluationDetail(record.userEvaluation);
  if (record.userEvaluation !== null && userEvaluation === null) {
    return null;
  }

  const trace: CognitoryActivityDetailView = {
    traceRequestId: summary.traceRequestId,
    capturedAt: summary.capturedAt,
    expiresAt: summary.expiresAt,
    durationMs: summary.durationMs,
    httpStatus: summary.httpStatus,
    responseDecision: summary.responseDecision,
    executionDecision: summary.executionDecision,
    attentionClass: summary.attentionClass,
    needsAttention: summary.needsAttention,
    question: summary.question,
    authorLabel: summary.authorLabel,
    evaluationStatus: summary.evaluationStatus,
    schemaVersion: COGNITORY_ACTIVITY_TRACE_SCHEMA_VERSION,
    response,
    userEvaluation,
  };
  if (diagnostics !== undefined) {
    trace.diagnostics = diagnostics;
  }
  return trace;
}

function readSupportReference(body: unknown): string | null {
  if (!body || typeof body !== "object") {
    return null;
  }
  const requestId = (body as { requestId?: unknown }).requestId;
  return isValidRequestId(requestId) ? requestId : null;
}

export function rebuildCognitoryActivityListPayload(input: {
  body: unknown;
  expectedCognitoryId: string;
}): CognitoryActivityListRebuildResult {
  if (treeContainsForbiddenKey(input.body)) {
    return {
      kind: "malformed_success",
      supportReference: readSupportReference(input.body),
    };
  }
  if (!input.body || typeof input.body !== "object") {
    return { kind: "malformed_success", supportReference: null };
  }
  const record = input.body as Record<string, unknown>;
  const supportReference = readSupportReference(record);
  if (record.decision !== "trace_summaries") {
    return { kind: "malformed_success", supportReference };
  }
  if (
    !isValidV2Id(record.cognitoryId) ||
    record.cognitoryId !== input.expectedCognitoryId
  ) {
    return { kind: "malformed_success", supportReference };
  }
  if (!isNonNegativeSafeInteger(record.limit) || record.limit < 1) {
    return { kind: "malformed_success", supportReference };
  }
  if (!Array.isArray(record.items)) {
    return { kind: "malformed_success", supportReference };
  }
  const items: CognitoryActivitySummaryView[] = [];
  for (const item of record.items) {
    const rebuilt = rebuildListSummaryItem(item, input.expectedCognitoryId);
    if (!rebuilt) {
      return { kind: "malformed_success", supportReference };
    }
    items.push(rebuilt);
  }
  let nextCursor: string | null = null;
  if (record.nextCursor !== undefined) {
    const cursor = boundedString(record.nextCursor, MAX_CURSOR_CHARS);
    if (!cursor) {
      return { kind: "malformed_success", supportReference };
    }
    nextCursor = cursor;
  }
  if (!supportReference) {
    return { kind: "malformed_success", supportReference: null };
  }
  return {
    kind: "success",
    supportReference,
    cognitoryId: record.cognitoryId,
    limit: record.limit,
    items,
    nextCursor,
  };
}

export function rebuildCognitoryActivityDetailPayload(input: {
  body: unknown;
  expectedCognitoryId: string;
  expectedTraceRequestId: string;
}): CognitoryActivityDetailRebuildResult {
  if (treeContainsForbiddenKey(input.body)) {
    return {
      kind: "malformed_success",
      supportReference: readSupportReference(input.body),
    };
  }
  if (!input.body || typeof input.body !== "object") {
    return { kind: "malformed_success", supportReference: null };
  }
  const record = input.body as Record<string, unknown>;
  const supportReference = readSupportReference(record);
  if (record.decision !== "trace_found") {
    return { kind: "malformed_success", supportReference };
  }
  const traceRaw = record.trace;
  if (!traceRaw || typeof traceRaw !== "object") {
    return { kind: "malformed_success", supportReference };
  }
  const trace = rebuildDetailTraceRecord(traceRaw, {
    expectedCognitoryId: input.expectedCognitoryId,
    expectedTraceRequestId: input.expectedTraceRequestId,
  });
  if (!trace) {
    return { kind: "malformed_success", supportReference };
  }
  if (!supportReference) {
    return { kind: "malformed_success", supportReference: null };
  }
  return {
    kind: "success",
    supportReference,
    trace,
  };
}

export function treeContainsCanonicalUserId(value: unknown): boolean {
  return treeContainsForbiddenKey(value);
}
